import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import { getMidnightOfExpiry } from 'src/lib/dateUtils';

dayjs.extend(utc);
dayjs.extend(duration);


const TrialExpiryPopup = () => {
    const history = useHistory();
    const [timeLeft, setTimeLeft] = useState({});
    const [showExpiryAlert, setShowExpiryAlert] = useState(false);
    const user = useSelector((state) => state.user.user);
    const subStatus = user?.subscriptionStatus;
    const trialExpiry = user?.freeUntilTimestamp;

    const showTimeoutRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        const activeStatuses = ["active", "authenticated", "created"];

        // helpers to clear timers safely
        const clearShowTimeout = () => {
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
                showTimeoutRef.current = null;
            }
        };
        const clearIntervalTimer = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        // clear timers on start of effect to avoid duplicates
        clearShowTimeout();
        clearIntervalTimer();

        if (activeStatuses.includes(subStatus)) {
            setShowExpiryAlert(false);
            return () => {
                clearShowTimeout();
                clearIntervalTimer();
            };
        }

        if (!trialExpiry) {
            return () => {
                clearShowTimeout();
                clearIntervalTimer();
            };
        }

        const expiryDate = getMidnightOfExpiry(trialExpiry);

        // Calculate the difference between now and the expiry date
        const diffInMillisec = expiryDate.diff(dayjs());
        const fiveDays = 5 * 24 * 60 * 60 * 1000;

        // Helper to calculate the time left
        const calculateTimeLeft = () => {
            const remaining = expiryDate.diff(dayjs());
            if (remaining <= 0) {
                // stop interval if running
                clearIntervalTimer();
                setTimeLeft({});
                setShowExpiryAlert(false);
                return {};
            }

            const durationObject = dayjs.duration(remaining);
            return {
                days: Math.floor(durationObject.asDays()),
                hours: durationObject.hours(),
                minutes: durationObject.minutes(),
                seconds: durationObject.seconds(),
            };
        };

        // Helper to schedule the popup after 10 seconds
        const schedulePopup = (initialTimeLeft) => {
            clearShowTimeout();
            // start timeout to show popup after 10 seconds
            showTimeoutRef.current = setTimeout(() => {
                // set initial timeLeft before showing
                setTimeLeft(initialTimeLeft);
                setShowExpiryAlert(true);

                // begin interval to update every second
                clearIntervalTimer();
                intervalRef.current = setInterval(() => {
                    setTimeLeft(calculateTimeLeft());
                }, 1000);
            }, 10000);
        };

        // If trial is over and status is 'expired', schedule 00:00:00 popup after 10s.
        if (diffInMillisec <= 0) {
            schedulePopup({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            return () => {
                clearShowTimeout();
                clearIntervalTimer();
            };
        }

        // Show the alert only if the remaining time is 5 days or less
        if (diffInMillisec <= fiveDays) {
            // Schedule show after 10s with an initial timeLeft calculated at the time of scheduling
            schedulePopup(calculateTimeLeft());

            return () => {
                clearShowTimeout();
                clearIntervalTimer();
            };
        }

        // If we are not within 5 days, nothing to do
        return () => {
            clearShowTimeout();
            clearIntervalTimer();
        };

    }, [trialExpiry, subStatus]);

    // Format the time to always have two digits (e.g., 04 instead of 4)
    const formatTime = (time) => time < 10 ? `0${time}` : time;

    const clockStyle = {
        border: '2px solid #6c5b7b',
        borderRadius: '8px',
        height: '70px',
        width: '90px',
        color: '#6c5b7b',
        paddingTop: '12px',
    }

    // Check if the timer has reached zero
    const timerComponents = Object.keys(timeLeft).length ? (
        <>
            <div className="time-box" style={clockStyle}>
                <span style={{ fontSize: '2rem', fontWeight: '700', }}>
                    {formatTime(timeLeft.days)}
                </span>
                <br />
                <span style={{ fontSize: 'small' }}>Days</span>
            </div>
            <div className="time-box" style={clockStyle}>
                <span style={{ fontSize: '2rem', fontWeight: '700', }}>
                    {formatTime(timeLeft.hours)}
                </span>
                <br />
                <span style={{ fontSize: 'small' }}>Hours</span>
            </div>
            <div className="time-box" style={clockStyle}>
                <span style={{ fontSize: '2rem', fontWeight: '700', }}>
                    {formatTime(timeLeft.minutes)}
                </span>
                <br />
                <span style={{ fontSize: 'small' }}>Minutes</span>
            </div>
            <div className="time-box" style={clockStyle}>
                <span style={{ fontSize: '2rem', fontWeight: '700', }}>
                    {formatTime(timeLeft.seconds)}
                </span>
                <br />
                <span style={{ fontSize: 'small' }}>Seconds</span>
            </div>
        </>
    ) : (
        <span>Time's up!</span>
    );

    const handleSubcribeNow = () => {
        history.push("/subscription");
        setShowExpiryAlert(false);
    }

    return (
        <>
            {/* subcription expiry alert for clinic */}
            <Modal
                size="lg"
                show={showExpiryAlert}
                centered
                aria-labelledby="example-modal-sizes-title-lg"
                onHide={() => setShowExpiryAlert(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="example-modal-sizes-title-lg" className="ml-3">
                        <span className="ModalTitle">
                            Reminder - Subscription Expiration Alert{" "}!!
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "82vh", overflow: "auto", }}>

                    <div style={{ textAlign: "center", fontSize: "1rem", padding: "1rem 1rem 3rem 1rem", }}>
                        <span style={{ fontSize: '2.5rem', fontWeight: '700' }}>⚠️ Your Free Trial Ends in</span>
                        <br />
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '30px' }}>
                            {timerComponents}
                        </div>

                        <span style={{ fontSize: '1.1rem', }}>
                            <br />
                            Don't lose access to DocG Al.
                            Subscribe now
                            <br />
                            to continue uninterrupted care and clinic management.
                        </span>
                    </div>

                    <div className="d-flex flex-column justify-content-center align-items-center gap-4 pb-4">
                        <button className="docg-save-btn" style={{ width: '60%' }} onClick={handleSubcribeNow}>
                            <span style={{ margin: 'auto', fontSize: '1.2rem' }}>Subscribe Now</span>
                        </button>
                        <button className="docg-outline-btn" style={{ width: '60%' }} onClick={() => setShowExpiryAlert(false)}>
                            <span style={{ margin: 'auto', fontSize: '1.2rem' }}>Remind Me Later</span>
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default TrialExpiryPopup;